import React, { useState, useEffect, useRef } from 'react';
import { Modal, Form, Button, Badge, Spinner, Alert } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import { API_URL } from '../../config/api';
import styles from './TicketDetailsModal.module.scss';

const TicketDetailsModal = ({ show, onHide, ticket, onTicketUpdated, onCloseTicket }) => {
  const [ticketData, setTicketData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [error, setError] = useState('');
  const messagesEndRef = useRef(null);

  const statusLabels = {
    open: { label: 'Aberto', color: 'warning', icon: 'mdi:clock-outline' },
    in_progress: { label: 'Em Andamento', color: 'info', icon: 'mdi:progress-clock' },
    waiting_user: { label: 'Aguardando Resposta', color: 'secondary', icon: 'mdi:account-clock' },
    resolved: { label: 'Resolvido', color: 'success', icon: 'mdi:check-circle' },
    closed: { label: 'Fechado', color: 'dark', icon: 'mdi:close-circle' }
  };

  const categoryLabels = {
    technical: { label: 'Técnico', icon: 'mdi:wrench', color: '#3B82F6' },
    financial: { label: 'Financeiro', icon: 'mdi:currency-usd', color: '#10B981' },
    administrative: { label: 'Administrativo', icon: 'mdi:account-supervisor', color: '#8B5CF6' },
    general: { label: 'Geral', icon: 'mdi:help-circle', color: '#6B7280' }
  };

  const priorityLabels = {
    low: { label: 'Baixa', color: '#10B981' },
    normal: { label: 'Normal', color: '#3B82F6' },
    high: { label: 'Alta', color: '#F59E0B' },
    urgent: { label: 'Urgente', color: '#EF4444' }
  };

  useEffect(() => {
    if (show && ticket) {
      loadTicketDetails();
    }
  }, [show, ticket]);

  useEffect(() => {
    scrollToBottom();
  }, [ticketData]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadTicketDetails = async () => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/tickets/${ticket.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erro ao carregar detalhes do ticket');
      }

      setTicketData(data.ticket);
    } catch (err) {
      console.error('Erro ao carregar detalhes:', err);
      setError(err.message || 'Erro ao carregar detalhes do ticket');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();

    if (!newMessage.trim()) {
      setError('Por favor, digite uma mensagem');
      return;
    }

    setSending(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/tickets/${ticket.id}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ message: newMessage })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erro ao enviar mensagem');
      }

      // Recarregar ticket
      await loadTicketDetails();
      
      // Limpar campo
      setNewMessage('');

      // Notificar atualização
      if (onTicketUpdated) {
        onTicketUpdated();
      }
    } catch (err) {
      console.error('Erro ao enviar mensagem:', err);
      setError(err.message || 'Erro ao enviar mensagem');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const canReply = ticketData && ticketData.status !== 'closed' && ticketData.status !== 'resolved';

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      backdrop="static"
      className={styles.ticketDetailsModal}
    >
      <Modal.Header closeButton className={styles.modalHeader}>
        <Modal.Title className={styles.modalTitle}>
          <Icon icon="mdi:ticket" className={styles.titleIcon} />
          Ticket #{ticket?.id}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className={styles.modalBody}>
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="text-muted mt-3">Carregando detalhes...</p>
          </div>
        ) : ticketData ? (
          <>
            {error && (
              <Alert variant="danger" dismissible onClose={() => setError('')} className="mb-3">
                <Icon icon="mdi:alert-circle" /> {error}
              </Alert>
            )}

            {/* Informações do Ticket */}
            <div className={styles.ticketInfo}>
              <div className={styles.infoRow}>
                <div className={styles.infoLabel}>
                  <Icon icon="mdi:format-list-bulleted" />
                  Categoria
                </div>
                <div className={styles.infoValue}>
                  <Icon
                    icon={categoryLabels[ticketData.category]?.icon}
                    style={{ color: categoryLabels[ticketData.category]?.color }}
                  />
                  {categoryLabels[ticketData.category]?.label}
                </div>
              </div>

              <div className={styles.infoRow}>
                <div className={styles.infoLabel}>
                  <Icon icon="mdi:flag" />
                  Prioridade
                </div>
                <div className={styles.infoValue}>
                  <Badge
                    style={{
                      backgroundColor: priorityLabels[ticketData.priority]?.color,
                      color: 'white'
                    }}
                  >
                    {priorityLabels[ticketData.priority]?.label}
                  </Badge>
                </div>
              </div>

              <div className={styles.infoRow}>
                <div className={styles.infoLabel}>
                  <Icon icon="mdi:information" />
                  Status
                </div>
                <div className={styles.infoValue}>
                  <Badge bg={statusLabels[ticketData.status]?.color}>
                    <Icon icon={statusLabels[ticketData.status]?.icon} className="me-1" />
                    {statusLabels[ticketData.status]?.label}
                  </Badge>
                </div>
              </div>

              <div className={styles.infoRow}>
                <div className={styles.infoLabel}>
                  <Icon icon="mdi:calendar" />
                  Criado em
                </div>
                <div className={styles.infoValue}>
                  {formatDate(ticketData.created_at)}
                </div>
              </div>
            </div>

            {/* Assunto */}
            <div className={styles.subjectBox}>
              <div className={styles.subjectLabel}>Assunto</div>
              <div className={styles.subjectText}>{ticketData.subject}</div>
            </div>

            {/* Mensagens */}
            <div className={styles.messagesSection}>
              <div className={styles.messagesHeader}>
                <Icon icon="mdi:message-text-outline" />
                Histórico de Conversas
              </div>

              <div className={styles.messagesList}>
                {ticketData.messages && ticketData.messages.length > 0 ? (
                  ticketData.messages.map((msg, index) => (
                    <div
                      key={msg.id}
                      className={`${styles.messageItem} ${
                        msg.is_admin_reply ? styles.adminMessage : styles.userMessage
                      }`}
                    >
                      <div className={styles.messageHeader}>
                        <div className={styles.messageAuthor}>
                          <Icon
                            icon={msg.is_admin_reply ? 'mdi:shield-account' : 'mdi:account'}
                            className={styles.authorIcon}
                          />
                          <strong>{msg.user_name || 'Você'}</strong>
                          {msg.is_admin_reply && (
                            <Badge bg="primary" className="ms-2">
                              Equipe Pagmus
                            </Badge>
                          )}
                        </div>
                        <div className={styles.messageDate}>
                          {formatDate(msg.created_at)}
                        </div>
                      </div>
                      <div className={styles.messageContent}>{msg.message}</div>
                    </div>
                  ))
                ) : (
                  <div className="text-center text-muted py-4">
                    <Icon icon="mdi:message-outline" size={48} className="mb-2" />
                    <p>Nenhuma mensagem ainda</p>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            </div>

            {/* Formulário de Resposta */}
            {canReply ? (
              <Form onSubmit={handleSendMessage} className={styles.replyForm}>
                <Form.Group>
                  <Form.Label className={styles.replyLabel}>
                    <Icon icon="mdi:reply" /> Sua Resposta
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Digite sua mensagem..."
                    className={styles.replyTextarea}
                    disabled={sending}
                  />
                </Form.Group>
                <div className="d-flex justify-content-end gap-2 mt-3">
                  <Button
                    variant="secondary"
                    onClick={onHide}
                    disabled={sending}
                  >
                    Fechar
                  </Button>
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={sending || !newMessage.trim()}
                    className="d-flex align-items-center gap-2"
                  >
                    {sending ? (
                      <>
                        <Spinner animation="border" size="sm" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Icon icon="mdi:send" />
                        Enviar Resposta
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            ) : (
              <div className={styles.closedNotice}>
                <Icon icon="mdi:information" size={24} />
                <p>Este ticket foi {ticketData.status === 'resolved' ? 'resolvido' : 'fechado'} e não aceita mais respostas.</p>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-5">
            <Icon icon="mdi:alert" size={64} className="text-muted mb-3" />
            <p className="text-muted">Não foi possível carregar os detalhes do ticket.</p>
          </div>
        )}
      </Modal.Body>
      
      {ticketData && ticketData.status !== 'closed' && ticketData.status !== 'resolved' && (
        <Modal.Footer className="d-flex justify-content-between">
          <Button variant="outline-secondary" onClick={onHide}>
            <Icon icon="mdi:close" className="me-2" />
            Fechar
          </Button>
          <Button 
            variant="danger" 
            onClick={() => {
              onCloseTicket(ticketData);
              onHide();
            }}
          >
            <Icon icon="mdi:close-circle" className="me-2" />
            Fechar Ticket
          </Button>
        </Modal.Footer>
      )}
    </Modal>
  );
};

export default TicketDetailsModal;

